import * as Stitches from '@stitches/react'

type ContainerSizes = 'sm' | 'md' | 'lg' | 'xl' | 'full'
type GridColumns = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
type GridColumnsSpan = GridColumns | 'auto'
type SpaceSizes = number

type TextOverflow = true | 'ellipsis' | 'clip'

const utils = {
  truncate: (value: TextOverflow) =>
    value === true
      ? {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }
      : { textOverflow: value },
  colSpan: (value: GridColumnsSpan) => ({
    gridColumn:
      typeof value === 'string' ? value : `span ${value} / span ${value}`
  }),
  gridCols: (value: GridColumns) => ({
    gridTemplateColumns: `repeat(${value}, minmax(0, 1fr))`
  }),
  container: (value: ContainerSizes) => {
    const base = { px: '$4', margin: '0 auto', '@md': { px: '$6' } }
    const maxWidths = new Map<ContainerSizes, number | string>([
      ['sm', 640],
      ['md', 768],
      ['lg', 1024],
      ['xl', 1280],
      ['full', '100%']
    ])

    return { ...base, maxWidth: maxWidths.get(value) }
  },
  spaceX: (value: SpaceSizes) => ({
    '&>:not([hidden])~:not([hidden])': {
      marginLeft: `calc(${value * 0.25}rem)`,
      marginRight: `calc(${value * 0.25}rem * 0)`
    }
  }),
  spaceY: (value: SpaceSizes) => ({
    '&>:not([hidden])~:not([hidden])': {
      marginTop: `calc(${value * 0.25}rem)`,
      marginBottom: `calc(${value * 0.25}rem * 0)`
    }
  }),
  inset: (value: Stitches.PropertyValue<'top'>) => ({
    top: value,
    right: value,
    left: value,
    bottom: value
  }),
  p: (value: Stitches.PropertyValue<'padding'>) => ({
    padding: value
  }),
  pt: (value: Stitches.PropertyValue<'paddingTop'>) => ({
    paddingTop: value
  }),
  pr: (value: Stitches.PropertyValue<'paddingTop'>) => ({
    paddingRight: value
  }),
  pb: (value: Stitches.PropertyValue<'paddingTop'>) => ({
    paddingBottom: value
  }),
  pl: (value: Stitches.PropertyValue<'paddingTop'>) => ({
    paddingLeft: value
  }),
  px: (value: Stitches.PropertyValue<'paddingTop'>) => ({
    paddingLeft: value,
    paddingRight: value
  }),
  py: (value: Stitches.PropertyValue<'paddingTop'>) => ({
    paddingTop: value,
    paddingBottom: value
  }),
  m: (value: Stitches.PropertyValue<'marginTop'>) => ({
    marginTop: value,
    marginBottom: value,
    marginLeft: value,
    marginRight: value
  }),
  mt: (value: Stitches.PropertyValue<'marginTop'>) => ({
    marginTop: value
  }),
  mr: (value: Stitches.PropertyValue<'marginTop'>) => ({
    marginRight: value
  }),
  mb: (value: Stitches.PropertyValue<'marginTop'>) => ({
    marginBottom: value
  }),
  ml: (value: Stitches.PropertyValue<'marginTop'>) => ({
    marginLeft: value
  }),
  mx: (value: Stitches.PropertyValue<'marginTop'>) => ({
    marginLeft: value,
    marginRight: value
  }),
  my: (value: Stitches.PropertyValue<'marginTop'>) => ({
    marginTop: value,
    marginBottom: value
  }),
  size: (value: Stitches.PropertyValue<'width'>) => ({
    width: value,
    height: value
  }),
  minSize: (value: Stitches.PropertyValue<'width'>) => ({
    minWidth: value,
    minHeight: value
  }),
  bgc: (value: Stitches.PropertyValue<'backgroundColor'>) => ({
    backgroundColor: value
  }),
  bgLinearGradient: (value: Stitches.PropertyValue<'backgroundImage'>) => ({
    backgroundImage: `linear-gradient(${value})`
  }),
  borderRadiusTop: (value: Stitches.PropertyValue<'borderRadius'>) => ({
    borderTopRightRadius: value,
    borderTopLeftRadius: value
  }),
  borderRadiusBottom: (value: Stitches.PropertyValue<'borderRadius'>) => ({
    borderBottomRightRadius: value,
    borderBottomLeftRadius: value
  }),
  borderRadiusLeft: (value: Stitches.PropertyValue<'borderRadius'>) => ({
    borderTopLeftRadius: value,
    borderBottomLeftRadius: value
  }),
  borderRadiusRight: (value: Stitches.PropertyValue<'borderRadius'>) => ({
    borderTopRightRadius: value,
    borderBottomRightRadius: value
  })
}

export default utils
